import React, { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-bottom: 1px solid var(--line-gray);
  padding: 0 30px;
`;

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  overflow-x: hidden;
  scroll-behavior: smooth;
  flex: 1;
  min-width: 0;
`;

const ArrowsContainer = styled.div`
  display: flex;
  margin-left: 10px;
  flex-shrink: 0;
`;

const ScrollButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--line-gray);
  background: white;
  cursor: pointer;
  padding: 0;

  &:first-child {
    border-radius: 4px 0 0 4px;
  }

  &:last-child {
    border-radius: 0 4px 4px 0;
    margin-left: -1px;
  }

  &.disabled {
    cursor: not-allowed;
  }

  img {
    width: 14px;
    height: 14px;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 10px 0;
  font-family: var(--font-regular);
  font-size: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;

  &.active {
    border-bottom-color: var(--text-primary);
    font-family: var(--font-bold);
    color: var(--text-primary);
  }
`;

const SCROLL_AMOUNT = 200;

interface Props {
  selectedCompetitorId: number | null;
  onTabChange: (competitorId: number | null) => void;
}

const SalesBattlecardTabs = ({ selectedCompetitorId, onTabChange }: Props) => {
  const { competitors, viewFilters } = useCommunityViewContext();
  const filteredCompetitors = (viewFilters as any)?.competitors?.length
    ? competitors?.filter((c: any) => (viewFilters as any).competitors.includes(c.id))
    : competitors;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflows = el.scrollWidth > el.clientWidth + 1;
    setIsOverflowing(overflows);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkOverflow);
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      observer.disconnect();
    };
  }, [checkOverflow, filteredCompetitors]);

  const handleScrollLeft = () => {
    if (canScrollLeft) {
      scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT });
    }
  };

  const handleScrollRight = () => {
    if (canScrollRight) {
      scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT });
    }
  };

  return (
    <Wrapper>
      <ScrollContainer ref={scrollRef}>
        <Tab className={classNames({ active: selectedCompetitorId === null })} onClick={() => onTabChange(null)}>
          Market Overview
        </Tab>
        {filteredCompetitors?.map((competitor: any) => (
          <Tab
            key={competitor.id}
            className={classNames({ active: selectedCompetitorId === competitor.id })}
            onClick={() => onTabChange(competitor.id)}
          >
            {competitor.comunity_name}
          </Tab>
        ))}
      </ScrollContainer>
      {isOverflowing && (
        <ArrowsContainer>
          <ScrollButton className={classNames({ disabled: !canScrollLeft })} onClick={handleScrollLeft}>
            <img
              src={canScrollLeft ? '/icons/chevron-left.svg' : '/icons/chevron-left-disabled.svg'}
              alt="scroll left"
            />
          </ScrollButton>
          <ScrollButton className={classNames({ disabled: !canScrollRight })} onClick={handleScrollRight}>
            <img
              src={canScrollRight ? '/icons/chevron-right.svg' : '/icons/chevron-right-disabled.svg'}
              alt="scroll right"
            />
          </ScrollButton>
        </ArrowsContainer>
      )}
    </Wrapper>
  );
};

export default SalesBattlecardTabs;
